"use client"

import { useState, useCallback, useEffect } from "react"
import { Heart, MessageSquare, ChevronDown, ChevronUp, Loader2, Trash2, Ellipsis } from "lucide-react"
import type { CommunityComment } from "@/lib/community/types"
import { relativeTime } from "@/lib/community/utils"
import { fetchReplies } from "@/lib/community/community-api"
import { subscribeNewReply, subscribeDeleteReply, emitDeleteReply } from "@/lib/community/reply-event"
import { CommunityAvatar } from "./community-avatar"
import { CommentImagePreview } from "./comment-image-preview"
import { CommentInput } from "./comment-input"

/** 从 content 中提取 <img src="..."> 里的 URL 列表，并返回纯文本部分 */
function parseContentAndImages(content: string): { text: string; imageUrls: string[] } {
  const imgRegex = /<img\s+src="([^"]+)"[^>]*>/gi
  const imageUrls: string[] = []
  const text = content.replace(imgRegex, (_, src: string) => {
    imageUrls.push(src)
    return ""
  }).trim()
  return { text, imageUrls }
}

interface CommentItemProps {
  comment: CommunityComment
  onLikeComment: (commentId: string) => void
  onDeleteComment?: (commentId: string) => Promise<void>
  /** 提交回复回调（内联回复用） */
  onSubmitComment?: (content: string, parentId?: string) => Promise<void>
  /** 当前全局激活的回复 ID（互斥控制） */
  activeReplyId?: string | null
  /** 设置全局激活的回复 ID */
  onActiveReplyChange?: (id: string | null) => void
  postAuthorId?: string
  /** 当前登录用户 ID，用于判断是否显示删除按钮 */
  currentUserId?: string
  /** 被回复者的显示名（仅子评论有值，用于显示"回复 xxx"） */
  parentAuthorName?: string
  /** 父评论 ID（用于删除子评论后通知父级更新 loadedReplies） */
  parentCommentId?: string
  /** 缩进层级，0=顶级 */
  depth?: number
}

/**
 * 递归评论组件——每条评论都能独立展开自己的子评论。
 * 参照 admin-web CommentRow 的做法：
 *   - 初始 replies ≤2 条时直接显示
 *   - replyTotal > 已有 replies 数量时显示展开按钮
 *   - 展开时调用 /comment/replies API 拿全量子评论，存在组件内部 state
 */
export function CommentItem({
  comment,
  onLikeComment,
  onDeleteComment,
  onSubmitComment,
  activeReplyId,
  onActiveReplyChange,
  postAuthorId,
  currentUserId,
  parentAuthorName,
  parentCommentId,
  depth = 0,
}: CommentItemProps) {
  const isReply = depth > 0
  const isPostAuthor = postAuthorId != null && comment.author.id === postAuthorId
  const isOwnComment = currentUserId != null && comment.author.id === currentUserId
  const { text: commentText, imageUrls } = parseContentAndImages(comment.content)
  const [deleting, setDeleting] = useState(false)

  // ── 内联回复（由全局 activeReplyId 控制，实现互斥） ──
  const isReplying = activeReplyId === comment.id
  const [inlineSubmitting, setInlineSubmitting] = useState(false)

  const handleToggleReply = useCallback(() => {
    // 所有层级的回复都使用内联输入框（互斥）
    onActiveReplyChange?.(isReplying ? null : comment.id)
  }, [isReplying, onActiveReplyChange, comment.id])

  const handleInlineSubmit = useCallback(async (content: string, parentId?: string) => {
    if (!onSubmitComment) return
    setInlineSubmitting(true)
    try {
      await onSubmitComment(content, parentId)
      onActiveReplyChange?.(null)
    } finally {
      setInlineSubmitting(false)
    }
  }, [onSubmitComment, onActiveReplyChange])

  // ── 子评论展开逻辑（参照 admin CommentRow） ──
  const [expanded, setExpanded] = useState(false)
  const [loadedReplies, setLoadedReplies] = useState<CommunityComment[]>(comment.replies ?? [])
  const [isLoadingReplies, setIsLoadingReplies] = useState(false)
  const [repliesLoaded, setRepliesLoaded] = useState((comment.replies?.length ?? 0) > 0)
  const replyTotal = comment.replyTotal ?? 0

  // 订阅新回复事件：当用户回复了本条评论，立即追加到本地 loadedReplies
  useEffect(() => {
    const unsubNew = subscribeNewReply(comment.id, (newReply) => {
      setLoadedReplies((prev) => {
        if (prev.some((r) => r.id === newReply.id)) return prev
        return [...prev, newReply]
      })
    })
    const unsubDel = subscribeDeleteReply(comment.id, (deletedId) => {
      setLoadedReplies((prev) => prev.filter((r) => r.id !== deletedId))
    })
    return () => { unsubNew(); unsubDel() }
  }, [comment.id])

  const handleToggleExpand = useCallback(async () => {
    if (expanded) {
      setExpanded(false)
      return
    }
    // 尚未加载过且有子评论：调接口全量获取
    if (!repliesLoaded && replyTotal > 0) {
      setIsLoadingReplies(true)
      try {
        const { replies } = await fetchReplies(
          comment.id,
          comment.postId,
          comment.author.displayName,
          1,
          replyTotal, // 一次拿完
        )
        setLoadedReplies(replies)
        setRepliesLoaded(true)
      } catch (err) {
        console.error("加载子评论失败", err)
      } finally {
        setIsLoadingReplies(false)
      }
    }
    setExpanded(true)
  }, [expanded, repliesLoaded, replyTotal, comment.id, comment.postId, comment.author.displayName])

  // 是否有子评论需要显示（inline ≤2 条 或 已展开）
  const hasReplies = loadedReplies.length > 0 || replyTotal > 0
  // 是否还有更多未加载的子评论
  const hasMoreReplies = replyTotal > loadedReplies.length && !repliesLoaded

  return (
    <div className="flex flex-col gap-2">
      {/* 评论本体 */}
      <div className="flex items-start gap-3">
        <CommunityAvatar
          src={comment.author.avatar}
          name={comment.author.displayName}
          sizeClass={isReply ? "w-7 h-7" : "w-8 h-8"}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-medium text-foreground ${isReply ? "text-xs" : "text-sm"}`}>
              {comment.author.displayName}
            </span>

            {isPostAuthor && (
              <span className="px-1.5 py-0.5 rounded text-micro font-medium bg-primary/15 text-primary">
                作者
              </span>
            )}

            {parentAuthorName && (
              <span className="text-xs text-muted-foreground">
                回复 <span className="text-foreground/80 font-medium">{parentAuthorName}</span>
              </span>
            )}

            <span className="text-xs text-muted-foreground">
              {relativeTime(comment.createdAt)}
            </span>
          </div>

          {commentText && (
            <p className={`text-foreground mt-0.5 leading-relaxed ${isReply ? "text-xs" : "text-sm"}`}>
              {commentText}
            </p>
          )}

          {imageUrls.length > 0 && (
            <CommentImagePreview images={imageUrls} />
          )}

          <div className="mt-1.5 flex items-center gap-3">
            <button
              onClick={() => onLikeComment(comment.id)}
              className={`inline-flex items-center gap-1 text-xs transition-colors ${comment.isLiked
                ? "text-red-500"
                : "text-muted-foreground hover:text-foreground"
                }`}
              aria-label={comment.isLiked ? "取消点赞" : "点赞"}
            >
              <Heart className={`h-3.5 w-3.5 ${comment.isLiked ? "fill-red-500" : ""}`} />
              <span>{comment.likes}</span>
            </button>

            {onActiveReplyChange && (
              <button
                onClick={handleToggleReply}
                className={`inline-flex items-center gap-1 text-xs transition-colors ${isReplying
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground"
                  }`}
                aria-label={isReplying ? "取消回复" : "回复"}
              >
                <MessageSquare className="h-3.5 w-3.5" />
                <span>{isReplying ? "取消回复" : "回复"}</span>
              </button>
            )}

            {isOwnComment && onDeleteComment && (
              <div className="ml-auto relative group/more">
                <button
                  className="inline-flex items-center justify-center h-6 w-6 rounded text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
                  aria-label="更多操作"
                >
                  <Ellipsis className="h-4 w-4" />
                </button>
                <div className="invisible opacity-0 group-hover/more:visible group-hover/more:opacity-100 transition-all duration-150 absolute right-0 bottom-full mb-1 z-50 min-w-[120px] rounded-md border border-border bg-popover p-1 shadow-md">
                  <button
                    onClick={async () => {
                      if (deleting) return
                      setDeleting(true)
                      try {
                        await onDeleteComment(comment.id)
                        if (parentCommentId) {
                          emitDeleteReply(parentCommentId, comment.id)
                        }
                      } catch {
                        setDeleting(false)
                      }
                    }}
                    disabled={deleting}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-red-500 hover:bg-secondary/60 rounded-sm transition-colors disabled:opacity-50 cursor-pointer"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    <span>{deleting ? "删除中…" : "删除"}</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* 内联回复输入框 */}
          {isReplying && onSubmitComment && (
            <div className="mt-2">
              <CommentInput
                onSubmit={handleInlineSubmit}
                replyTo={{ commentId: comment.id, authorName: comment.author.displayName }}
                onCancelReply={() => onActiveReplyChange?.(null)}
                submitting={inlineSubmitting}
                compact
                autoFocus
              />
            </div>
          )}
        </div>
      </div>

      {/* 子评论区域：只在顶级评论(depth=0)下缩进，更深层保持同级避免越来越窄 */}
      {hasReplies && (
        <div className={depth === 0 ? "ml-11 flex flex-col gap-2 border-l border-border/20" : "flex flex-col gap-2"}>
          {/* 默认显示 inline replies（≤2条），展开后显示全量 */}
          {(expanded || loadedReplies.length > 0) && loadedReplies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              onLikeComment={onLikeComment}
              onDeleteComment={onDeleteComment}
              onSubmitComment={onSubmitComment}
              activeReplyId={activeReplyId}
              onActiveReplyChange={onActiveReplyChange}
              postAuthorId={postAuthorId}
              currentUserId={currentUserId}
              parentCommentId={comment.id}
              parentAuthorName={reply.parentAuthorName ?? comment.author.displayName}
              depth={depth + 1}
            />
          ))}

          {/* 展开/折叠按钮 */}
          {hasMoreReplies && (
            <button
              type="button"
              onClick={handleToggleExpand}
              disabled={isLoadingReplies}
              className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors self-start py-0.5"
            >
              {isLoadingReplies
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <ChevronDown className="h-3 w-3" />
              }
              <span>
                {isLoadingReplies ? "加载中…" : `共 ${replyTotal} 条回复，点击展开`}
              </span>
            </button>
          )}

          {/* 已展开 & 已加载完毕，可以折叠 */}
          {expanded && repliesLoaded && !hasMoreReplies && loadedReplies.length > 2 && (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors self-start py-0.5"
            >
              <ChevronUp className="h-3 w-3" />
              <span>收起</span>
            </button>
          )}
        </div>
      )}
    </div>
  )
}
